import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { AppShell } from "@/components/TopNav";
import { PageHeader, HalalBadge, Delta, EmptyState, ComplianceBadge } from "@/components/common";
import { InvestModule } from "@/pages/Invest";
import { ComplianceReport } from "@/components/ComplianceReport";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { fmtCcy, fmtUSD } from "@/lib/format";
import { useNavigate } from "react-router-dom";
import { ShieldCheck, Calculator, Award, BookOpen, FileSearch } from "lucide-react";

export default function Halal() {
  const navigate = useNavigate();
  const [sukuk, setSukuk] = useState([]);
  const [screening, setScreening] = useState([]);
  const [cert, setCert] = useState(null);
  const [zakat, setZakat] = useState({ cash: 20000, investments: 30000, gold_value: 0, debts: 0 });
  const [zakatResult, setZakatResult] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [report, setReport] = useState({ open: false, symbol: null });

  useEffect(() => {
    api.get("/halal/sukuk").then((r) => setSukuk(r.data));
    api.get("/halal/screening").then((r) => setScreening(r.data));
    api.get("/halal/certification").then((r) => setCert(r.data));
  }, []);

  const calcZakat = async () => {
    const { data } = await api.post("/halal/zakat", zakat);
    setZakatResult(data);
  };

  const filteredScreening = screening.filter((s) => statusFilter === "all" || s.compliance.status === statusFilter);
  const counts = screening.reduce((a, s) => { a[s.compliance.status] = (a[s.compliance.status] || 0) + 1; return a; }, {});

  return (
    <AppShell>
      <PageHeader title="Halal Investing" subtitle="Shariah-compliant portfolios, Sukuk & screening"
        action={<HalalBadge />} testid="halal-header" />

      <Card className="p-5 shadow-premium card-premium mb-5" data-testid="halal-board-cert">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-lg bg-emerald-50 flex items-center justify-center"><Award className="h-6 w-6 text-emerald-600" /></div>
            <div><div className="font-semibold">{cert?.board_name}</div>
              <div className="text-xs text-muted-foreground">Certificate {cert?.certificate_id} · Last audit {cert?.last_audit}</div></div>
          </div>
          <div className="flex flex-wrap gap-2">{cert?.standards?.map((s) => <Badge key={s} variant="secondary">{s}</Badge>)}</div>
        </div>
      </Card>

      <Tabs defaultValue="robo">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="robo">Shariah Robo-Advisor</TabsTrigger>
          <TabsTrigger value="sukuk">Sukuk ETFs</TabsTrigger>
          <TabsTrigger value="screen" data-testid="halal-screener-tab">Equity Screening</TabsTrigger>
          <TabsTrigger value="zakat" data-testid="zakat-tab">Zakat Calculator</TabsTrigger>
          <TabsTrigger value="board">Shariah Board</TabsTrigger>
        </TabsList>

        <TabsContent value="robo" className="mt-4"><InvestModule kind="halal" /></TabsContent>

        <TabsContent value="sukuk" className="mt-4">
          <Card className="p-4 shadow-premium">
            <div className="text-sm font-medium mb-3">Sukuk & Islamic Fixed Income</div>
            <Table><TableHeader><TableRow><TableHead>Symbol</TableHead><TableHead>Name</TableHead><TableHead className="text-end">Price</TableHead><TableHead className="text-end">Change</TableHead><TableHead></TableHead></TableRow></TableHeader>
              <TableBody>{sukuk.map((s, si) => (<TableRow key={`${s.symbol}-${si}`}>
                <TableCell className="font-medium">{s.symbol}</TableCell><TableCell className="text-muted-foreground">{s.name}</TableCell>
                <TableCell className="text-end numeric">{fmtCcy(s.price, s.currency)}</TableCell><TableCell className="text-end"><Delta value={s.change_pct} /></TableCell>
                <TableCell className="text-end"><Button size="sm" variant="secondary" onClick={() => navigate(`/stock/${encodeURIComponent(s.symbol)}`)}>View</Button></TableCell>
              </TableRow>))}</TableBody></Table>
          </Card>
        </TabsContent>

        <TabsContent value="screen" className="mt-4">
          <Card className="p-4 shadow-premium" data-testid="halal-screener">
            <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
              <div className="text-sm font-medium">Halal Equity Screening</div>
              <div className="flex flex-wrap gap-1.5">
                {[["all", `All (${screening.length})`], ["Compliant", `Shariah Compliant (${counts["Compliant"] || 0})`], ["Questionable", `Questionable (${counts["Questionable"] || 0})`], ["Non-Compliant", `Shariah Non-Compliant (${counts["Non-Compliant"] || 0})`]].map(([v, label]) => (
                  <button key={v} onClick={() => setStatusFilter(v)}
                    className={`rounded-md border px-2.5 py-1 text-xs font-medium transition-colors ${statusFilter === v ? "bg-secondary border-[color:var(--tharwy-lime)]" : "hover:bg-accent"}`}
                    data-testid={`screen-filter-${v}`}>{label}</button>
                ))}
              </div>
            </div>
            <Table><TableHeader><TableRow><TableHead>Symbol</TableHead><TableHead>Status</TableHead><TableHead className="text-end">Debt ratio</TableHead><TableHead className="text-end">Non-perm income</TableHead><TableHead className="text-end">Report</TableHead></TableRow></TableHeader>
              <TableBody>{filteredScreening.map((s, i) => (<TableRow key={`${s.symbol}-${i}`}>
                <TableCell><div className="font-medium">{s.symbol}</div><div className="text-xs text-muted-foreground truncate max-w-[160px]">{s.name}</div></TableCell>
                <TableCell><ComplianceBadge status={s.compliance.status} /></TableCell>
                <TableCell className="text-end numeric">{s.compliance.debt_ratio}%</TableCell>
                <TableCell className="text-end numeric">{s.compliance.interest_income_pct}%</TableCell>
                <TableCell className="text-end"><Button size="sm" variant="secondary" className="gap-1.5" onClick={() => setReport({ open: true, symbol: s.symbol })} data-testid={`report-btn-${s.symbol}`}><FileSearch className="h-3.5 w-3.5" />View</Button></TableCell>
              </TableRow>))}</TableBody></Table>
          </Card>
        </TabsContent>

        <TabsContent value="zakat" className="mt-4">
          <div className="grid gap-4 lg:grid-cols-2">
            <Card className="p-5 shadow-premium" data-testid="zakat-calculator">
              <div className="flex items-center gap-2 text-sm font-medium mb-3"><Calculator className="h-4 w-4" />Zakat Calculator</div>
              <div className="space-y-3">
                {[["cash","Cash & bank"],["investments","Investments"],["gold_value","Gold value"],["debts","Debts (deduct)"]].map(([k,label]) => (
                  <div key={k} className="space-y-1.5"><Label>{label} (USD)</Label>
                    <Input type="number" value={zakat[k]} onChange={(e) => setZakat({ ...zakat, [k]: Number(e.target.value) })} data-testid={`zakat-${k}`} /></div>
                ))}
                <Button className="w-full" onClick={calcZakat} data-testid="zakat-calc-button">Calculate Zakat</Button>
              </div>
            </Card>
            <Card className="p-5 shadow-premium">
              <div className="text-sm font-medium mb-3">Result</div>
              {zakatResult ? (
                <div className="space-y-3">
                  <div className="rounded-lg bg-secondary/60 p-4"><div className="text-xs text-muted-foreground">Zakatable wealth</div><div className="numeric text-xl font-semibold">{fmtUSD(zakatResult.zakatable_wealth)}</div></div>
                  <div className="rounded-lg p-4" style={{ background: "rgba(14,143,110,0.1)" }}><div className="text-xs text-muted-foreground">Zakat due (2.5%)</div><div className="numeric text-2xl font-semibold text-emerald-600" data-testid="zakat-result">{fmtUSD(zakatResult.zakat_due)}</div></div>
                  <div className="text-xs text-muted-foreground">Nisab threshold: {fmtUSD(zakatResult.nisab_usd)}. {zakatResult.above_nisab ? "Your wealth is above nisab — zakat is due." : "Below nisab — no zakat due."}</div>
                </div>
              ) : <EmptyState title="Enter your assets" hint="Calculate the zakat due on your wealth" />}
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="board" className="mt-4">
          <Card className="p-5 shadow-premium">
            <div className="flex items-center gap-2 text-sm font-medium mb-3"><BookOpen className="h-4 w-4" />Shariah Supervisory Board</div>
            <p className="text-sm text-muted-foreground mb-4">{cert?.statement}</p>
            <div className="grid sm:grid-cols-3 gap-3">{cert?.scholars?.map((s) => (
              <div key={s.name} className="rounded-lg border p-4"><div className="font-medium">{s.name}</div><div className="text-xs text-[color:var(--tharwy-lime-700)] font-medium">{s.role}</div><div className="text-xs text-muted-foreground mt-1">{s.credentials}</div></div>
            ))}</div>
          </Card>
        </TabsContent>
      </Tabs>

      <ComplianceReport open={report.open} onOpenChange={(v) => setReport({ ...report, open: v })} symbol={report.symbol} />
    </AppShell>
  );
}
